import { Component, Input } from '@angular/core';

import { Product } from '../../../models/product.model';

/**
 * Toggle "Sustentable" + selección múltiple de esquemas de sustentabilidad
 * (EPA, 2BSvs, RTRS, EUDR, etc.) para [[SlotsBatch|Lotes]]. Sólo se muestra
 * para productos con esquemas de sustentabilidad (como en el formulario de
 * órdenes).
 *
 * Muta `quality` in-place: `{ sustainable: boolean, sustainable_schemes?: [{...}] }`.
 * Las opciones salen de `product.attributes.quality.sustainable`, igual que en
 * la creación de órdenes. La opción vacía ("No especifica") no se ofrece: sin
 * esquemas elegidos equivale a no especificar.
 */
@Component({
  selector: 'ag-sustainable-input',
  templateUrl: './sustainable-input.component.html',
  styles: [`
    .sustainable-input { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 6px 24px; margin-bottom: 15px; }
    .sustainable-input .checkbox { margin: 0; min-height: 34px; }
    .sustainable-input .scheme { min-width: 200px; }
    .sustainable-input .scheme .control-label { display: block; }
    .sustainable-input.compact { align-items: center; gap: 4px 12px; margin: 6px 0 0; }
    .sustainable-input .compact-toggle { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-weight: normal; cursor: pointer; }
    .sustainable-input .compact-toggle input { margin: 0; }
    .sustainable-input.compact .scheme { min-width: 140px; }
  `]
})
export class SustainableInputComponent {

  @Input() public quality: { sustainable?: boolean, sustainable_schemes?: any[] };
  @Input() public disabled: boolean;
  /** Usado para generar ids únicos cuando hay más de una instancia en pantalla. */
  @Input() public name: string = 'sustainable';
  /** Versión reducida para filas de tablas: checkbox simple y select chico, sin título. */
  @Input() public compact: boolean;

  @Input() public set product(p: Product) {
    const previousId = this._product?.id;
    this._product = p;

    if (previousId && p?.id !== previousId && this.quality) {
      // Las opciones dependen del producto; si el nuevo no admite esquemas el
      // toggle se oculta, así que no debe quedar marcado.
      if (!this.scheme) this.quality.sustainable = false;
      this.quality.sustainable_schemes = this.quality.sustainable ? [] : undefined;
    }
  }
  public get product(): Product {
    return this._product;
  }

  private _product: Product;

  /** Atributo de calidad del esquema de sustentabilidad del producto, si existe. */
  public get scheme(): any {
    return this._product?.attributes?.quality?.sustainable;
  }

  /**
   * Mismo criterio que el formulario de órdenes: si el atributo tiene opción
   * vacía ("No especifica") se respeta el orden del catálogo, si no, por nombre.
   */
  public get options(): any[] {
    const values: any[] = this.scheme?.values || [];

    return this.scheme?.empty ? values : values.slice().sort((a, b) => a.name.localeCompare(b.name));
  }

  public toggle(value: boolean): void {
    this.quality.sustainable = value;
    this.quality.sustainable_schemes = value ? (this.quality.sustainable_schemes || []) : undefined;
  }
}
